import { useEffect, useState } from "react";
import supabase from "../config/supabaseConfig";

// Define types
export interface Category {
  id: string;
  name: string;
  image: string;
  adCount?: number;
}

export interface Subcategory {
  id: string;
  name: string;
  image: string;
  category_id: string;
  adCount?: number;
  parentCategoryName?: string;
}

const useFetchAllCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategoriesAndCounts = async () => {
      setLoading(true);
      try {
        // Fetch categories
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('categories')
          .select('*');

        if (categoriesError) throw categoriesError;

        // Fetch subcategories
        const { data: subcategoriesData, error: subcategoriesError } = await supabase
          .from('subcategories')
          .select('*');

        if (subcategoriesError) throw subcategoriesError;

        // Count active ads per category/subcategory server-side via RPC
        // instead of fetching every active ad's category_id/subcategory_id
        // client-side. The old approach pulled one row per active ad with
        // no .limit(), so counts would silently fall short (and the
        // request would keep growing) as the ads table scales past the
        // default page size. count_ads_by_category/count_ads_by_subcategory
        // mirror the existing count_ads_by_state/count_ads_by_city RPCs
        // used in useFetchAllLocations.ts.
        const [
          { data: categoryAdCounts, error: categoryCountError },
          { data: subcategoryAdCounts, error: subcategoryCountError },
        ] = await Promise.all([
          supabase.rpc('count_ads_by_category'),
          supabase.rpc('count_ads_by_subcategory'),
        ]);

        if (categoryCountError || subcategoryCountError) {
          console.error("Error fetching ad counts:", categoryCountError || subcategoryCountError);
        }

        // Build maps of counts for easier lookup
        const categoryCountMap: Record<string, number> = {};
        const subcategoryCountMap: Record<string, number> = {};

        categoryAdCounts?.forEach((row: { category_id: string; count: number }) => {
          if (row.category_id) categoryCountMap[row.category_id] = row.count;
        });
        subcategoryAdCounts?.forEach((row: { subcategory_id: string; count: number }) => {
          if (row.subcategory_id) subcategoryCountMap[row.subcategory_id] = row.count;
        });

        // Combine the data with counts
        const categoriesWithCounts = (categoriesData || []).map(category => ({
          ...category,
          adCount: categoryCountMap[category.id] || 0
        }));

        const subcategoriesWithCounts = (subcategoriesData || []).map(subcategory => ({
          ...subcategory,
          adCount: subcategoryCountMap[subcategory.id] || 0
        }));

        // Add parent category names to subcategories for display purposes
        const enrichedSubcategories = subcategoriesWithCounts.map(subcat => {
          const parentCategory = categoriesWithCounts.find(cat => cat.id === subcat.category_id);
          return {
            ...subcat,
            parentCategoryName: parentCategory ? parentCategory.name : undefined
          };
        });

        setCategories(categoriesWithCounts);
        setSubcategories(enrichedSubcategories);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      } finally {
        setLoading(false);
      }
    };

    fetchCategoriesAndCounts();
  }, []);

  return { categories, subcategories, loading, error };
};

export default useFetchAllCategories;
